// ============================================================
// zustand store — the React re-render driver over the headless engine.
// Holds a `version` counter; bump() forces subscribers to re-read the
// engine. View navigation is backed by React Router (registerNavigate).
// ============================================================
import { create } from "zustand";
import { eng } from "@/lib/engineApi";

export type ViewId = "overview" | "rfi" | "sitemap" | "qc" | "punch" | "tdd";
export const VIEWS: ViewId[] = ["overview", "rfi", "sitemap", "qc", "punch", "tdd"];

// React Router's navigate(), registered by <App> so store actions can route.
let _navigate: ((to: string) => void) | null = null;
export function registerNavigate(fn: (to: string) => void): void {
  _navigate = fn;
}

// Per-role view access. Roles not listed see every view (inspector, qcmanager,
// auditor). The restricted roles only reach a curated subset:
//   tdd        — overview + documents (mirrors the engine's original gate)
//   contractor — their NCRs/RFIs + the map (no full document register)
//   client     — read-only dashboard, map, registers, documents (no QC tool)
const ROLE_VIEWS: Record<string, ViewId[]> = {
  tdd: ["overview", "tdd"],
  contractor: ["overview", "sitemap", "qc", "rfi", "punch"],
  client: ["overview", "sitemap", "rfi", "punch", "tdd"],
};

export function allowed(view: ViewId, role: string): boolean {
  const set = ROLE_VIEWS[role];
  return set ? set.includes(view) : true;
}

interface GridState {
  version: number;
  view: ViewId;
  loggedIn: boolean;
  bump: () => void;
  /** Navigate to a view (updates the URL via the router). */
  setView: (v: ViewId) => void;
  /** Sync store + engine to the current URL (called by App on location change). */
  syncView: (v: ViewId) => void;
  /** Pick an in-app role and enter the platform. */
  enter: (role: string) => void;
}

export const useGrid = create<GridState>((set) => ({
  version: 0,
  view: "overview",
  loggedIn: false,
  bump: () => set((s) => ({ version: s.version + 1 })),
  setView: (v) => {
    const e = eng();
    if (!allowed(v, e.currentRole)) return;
    if (_navigate) _navigate("/" + v);
    e.state.view = v;
    set((s) => ({ view: v, version: s.version + 1 }));
  },
  syncView: (v) => {
    const e = eng();
    const target: ViewId = allowed(v, e.currentRole) ? v : "overview";
    if (target !== v && _navigate) _navigate("/" + target);
    e.state.view = target;
    set((s) => ({ view: target, version: s.version + 1 }));
  },
  enter: (role) => {
    const e = eng();
    e.setRole(role);
    e.login();
    e.saveToStorage();
    // Honour a deep-linked path through the role gate; else go to overview.
    const path = (typeof location !== "undefined" ? location.pathname.replace(/^\//, "") : "") as ViewId;
    const target: ViewId = VIEWS.includes(path) && allowed(path, role) ? path : "overview";
    if (_navigate) _navigate("/" + target);
    e.state.view = target;
    set((s) => ({ loggedIn: true, view: target, version: s.version + 1 }));
  },
}));

/** Install the engine → React notify bridge (engine.render() calls this). */
export function installNotify(): void {
  (window as any).__gpComponentMode = true;
  (window as any).__gpNotify = () => useGrid.getState().bump();
}
